import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import styled, { createGlobalStyle, ThemeContext } from 'styled-components';
import messagesService from '../../api/messages';
import CommentList from './CommentList';
import { useAuth } from '../../hooks/useAuth';
import { formatTimeAgo } from '../../utils/formatters';
import AuthModal from '../auth/AuthModal';
import html2canvas from 'html2canvas';
import { useUniversity } from '../../contexts/UniversityContext';
import { useWebSocket } from '../../hooks/useWebSocket';

const MessageContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.divider};
  padding: 16px;
  transition: background-color 0.2s;
  opacity: ${props => props.$initialRender ? 0.95 : 1};
  width: 100%;
  box-sizing: border-box;
  margin-bottom: ${props => props.$level === 0 ? '14px' : props.$level === 1 ? '12px' : '10px'};
  display: flex;

  &:hover {
    background-color: ${props => props.theme.messageHover};
  }
  
  @media (max-width: 992px) {
    padding: 14px;
  }
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 12px;
  
  @media (max-width: 480px) {
    margin-left: 10px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1877f2, #4267B2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
  font-size: 22px;
  
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 20px;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    gap: 1px;
    margin-bottom: 6px;
  }
`;

const Username = styled.div`
  font-weight: 600;
  color: #1877f2;
  font-size: 15px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const Timestamp = styled.div`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const MessageText = styled.div`
  color: ${props => props.theme.text};
  font-size: 15px;
  line-height: 1.4;
  margin-bottom: ${props => props.$hasImage ? '8px' : '12px'};
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: ${props => props.$hasImage ? '6px' : '10px'};
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: ${props => props.$hasImage ? '4px' : '8px'};
  }
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  width: 100%;
  
  @media (max-width: 992px) {
    margin-top: 4px;
  }
  
  @media (max-width: 768px) {
    margin-top: 3px;
  }
  
  @media (max-width: 480px) {
    margin-top: 2px;
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  margin-left: 0;
  margin-bottom: 16px;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${props => props.theme.divider};
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.imageBackground || '#f0f2f5'};
  min-height: 200px;
  
  @media (max-width: 768px) {
    min-height: 180px;
    margin-bottom: 14px;
  }
  
  @media (max-width: 480px) {
    min-height: 160px;
    margin-bottom: 12px;
  }
`;

const MessageImage = styled.img.attrs({
  crossOrigin: "anonymous"
})`
  width: 100%;
  height: 100%;
  max-height: 350px;
  border-radius: 12px;
  cursor: zoom-in;
  object-fit: cover;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 992px) {
    max-height: 300px;
  }
  
  @media (max-width: 768px) {
    max-height: 250px;
  }
  
  @media (max-width: 480px) {
    max-height: 200px;
    border-radius: 10px;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 8px 12px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.$active ? props.$activeColor : props.theme.textSecondary};
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
  max-width: 33.33%;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    width: 18px;
    height: 18px;
    margin-right: 6px;
    transition: transform 0.2s ease;
    color: ${props => props.$active ? props.$activeColor : 'inherit'};
    flex-shrink: 0;
    
    @media (max-width: 768px) {
      width: 16px;
      height: 16px;
      margin-right: 5px;
    }
    
    @media (max-width: 480px) {
      width: 15px;
      height: 15px;
      margin-right: 4px;
    }
  }
  
  &:hover {
    background-color: ${props => props.theme.buttonHover};
    
    svg {
      transform: ${props => props.$hoverAnimation ? 'scale(1.1)' : 'none'};
    }
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 12px;
    border-radius: 12px;
  }
`;

const StatCount = styled.span`
  margin-left: 4px;
  font-size: 14px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  color: ${props => props.$active ? props.$activeColor || props.theme.primary : 'inherit'};
  transition: color 0.2s ease, font-weight 0.1s ease;
  flex-shrink: 0;
  white-space: nowrap;
  
  @media (max-width: 992px) {
    font-size: 13px;
    margin-left: 3px;
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-left: 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    margin-left: 2px;
  }
`;

const HashtagHighlight = styled.span`
  color: ${props => props.theme.hashtag};
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FullImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'default'};
  padding: clamp(10px, 3vw, 30px);
  animation: fadeIn 0.3s ease;
  backdrop-filter: blur(3px);
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const FullImageContainer = styled.div`
  position: relative;
  max-width: min(90vw, 1200px);
  max-height: 90vh;
  width: auto;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease;
  border-radius: 16px;
  overflow: ${props => props.$zoomed ? 'hidden' : 'visible'};
  background: ${props => props.theme.card};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  &:hover .image-controls {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    max-width: 95vw;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: ${props => props.$zoomed ? 'auto' : 'hidden'};
  cursor: ${props => props.$zoomed ? (props.$isDragging ? 'grabbing' : 'grab') : 'zoom-in'};
  background-color: ${props => props.theme.mode === 'dark' ? '#242526' : '#ffffff'};
  border-radius: inherit;
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const FullImage = styled.img.attrs({
  crossOrigin: "anonymous"
})`
  max-width: 100%;
  max-height: 90vh;
  height: auto;
  object-fit: contain;
  animation: scaleIn 0.3s ease;
  transform: ${props => props.$zoomed ? 'scale(1.8)' : 'scale(1)'};
  transform-origin: ${props => props.$transformOrigin || 'center center'};
  transition: transform 0.3s ease;
  padding: ${props => props.$isVector ? '40px' : '0'};
  box-sizing: border-box;
  background-color: ${props => props.$isVector ? '#ffffff' : 'transparent'};
  border-radius: inherit;
  
  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0.5; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.$isVector ? '20px' : '0'};
  }
`;

const ImageControls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: clamp(10px, 3vw, 20px);
  display: flex;
  justify-content: space-between;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%);
  border-radius: inherit;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  
  @media (max-width: 768px) {
    opacity: 1;
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const ControlButton = styled.button`
  background-color: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  width: clamp(32px, 5vw, 40px);
  height: clamp(32px, 5vw, 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: clamp(16px, 2.5vw, 20px);
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: clamp(16px, 2.5vw, 20px);
    height: clamp(16px, 2.5vw, 20px);
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: clamp(8px, 1.5vw, 12px);
`;

const GlobalStyle = createGlobalStyle`
  @media (max-width: 600px) {
    .action-text {
      display: none;
    }
  }
`;

// Styled components para el modal de compartir
const ShareModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: clamp(10px, 3vw, 20px);
  animation: fadeIn 0.3s ease;
  backdrop-filter: blur(3px);
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ShareModalContent = styled.div`
  background-color: ${props => props.theme.card};
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid ${props => props.theme.divider};
  position: relative;
  margin: 0 10px;
  
  @keyframes popIn {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @media (max-width: 768px) {
    max-width: 95%;
    margin: 0;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    max-height: 85vh;
    margin: 0;
  }
`;

const ShareModalHeader = styled.div`
  padding: clamp(14px, 3vw, 18px) clamp(16px, 3vw, 20px);
  border-bottom: 1px solid ${props => props.theme.divider};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme.primary};
  color: white;
  border-radius: 16px 16px 0 0;
  
  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

const ShareModalTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: clamp(18px, 3vw, 20px);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    width: clamp(20px, 3vw, 24px);
    height: clamp(20px, 3vw, 24px);
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const ShareModalBody = styled.div`
  padding: clamp(16px, 3vw, 20px);
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SharePreviewImage = styled.img.attrs({
  crossOrigin: "anonymous"
})`
  max-width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.theme.divider};
  margin: 10px 0;
`;

const ShareModalFooter = styled.div`
  padding: clamp(14px, 3vw, 20px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-top: 1px solid ${props => props.theme.divider};
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 8px;
  }
`;

const CancelButton = styled.button`
  background-color: ${props => props.theme.buttonLight};
  color: ${props => props.theme.buttonText};
  border: none;
  border-radius: 8px;
  padding: clamp(10px, 2vw, 12px) clamp(16px, 3vw, 20px);
  font-weight: 500;
  font-size: clamp(14px, 2vw, 15px);
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  
  &:hover {
    background-color: ${props => props.theme.buttonLightHover};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ShareButton = styled.button`
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  padding: clamp(10px, 2vw, 12px) clamp(16px, 3vw, 20px);
  font-weight: 600;
  font-size: clamp(14px, 2vw, 15px);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 2;
  
  &:hover {
    background-color: #43A047;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: clamp(16px, 2.5vw, 18px);
    height: clamp(16px, 2.5vw, 18px);
    margin-right: 8px;
    
    @media (max-width: 480px) {
      margin-right: 6px;
    }
  }
`;

const ShareMessage = styled.div`
  color: ${props => props.theme.textSecondary};
  text-align: center;
  margin-bottom: 15px;
  font-size: clamp(14px, 2vw, 15px);
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const MessageItem = ({ message, onHashtagClick, onLikeUpdate }) => {
  // Ensure likesCount is initialized with a proper numeric value
  const initialLikesCount = typeof message.likes_count === 'number' ? message.likes_count : 
                           typeof message.likes === 'number' ? message.likes : 0;
  
  const [liked, setLiked] = useState(message.liked || false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [shared, setShared] = useState(message.shared || false);
  const [sharesCount, setSharesCount] = useState(message.shares_count || message.shares || 0);
  const [showComments, setShowComments] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [initialRender, setInitialRender] = useState(true);
  const [likeButtonCooldown, setLikeButtonCooldown] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [transformOrigin, setTransformOrigin] = useState('0 0');
  const imageRef = useRef(null);
  
  const messageRef = useRef(null);
  const { isAuthenticated } = useAuth();
  const theme = useContext(ThemeContext);
  const { currentUniversity } = useUniversity();
  const { addMessageHandler } = useWebSocket();
  
  // Handle escape key to close full image view
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showFullImage) {
        setShowFullImage(false);
      }
    };
    
    if (showFullImage) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showFullImage]);
  
  // Marcamos que el componente ya ha realizado su renderizado inicial
  useEffect(() => {
    if (initialRender) {
      setTimeout(() => {
        setInitialRender(false);
      }, 50);
    }
  }, [initialRender]);
  
  useEffect(() => {
    setLiked(message.liked || false);
    setLikesCount(initialLikesCount);
    setShared(message.shared || false);
    setSharesCount(message.shares_count || message.shares || 0);
  }, [message, initialLikesCount]);
  
  // Check for stored like status in session storage on component mount
  useEffect(() => {
    if (isAuthenticated && message.id && !userInteracted) {
      try {
        let messageId = message.id;
        if (typeof messageId === 'object' && messageId._id) {
          messageId = messageId._id;
        }
        
        const storageKey = `message_like_${messageId}`;
        const storedLikeStatus = sessionStorage.getItem(storageKey);
        
        if (storedLikeStatus !== null) {
          const isLiked = storedLikeStatus === 'true';
          setLiked(isLiked);
          // No actualizamos el contador aquí, confiamos en el valor del servidor
        }
      } catch (error) {
        console.error('Error retrieving like status from session storage:', error);
      }
    }
  }, [isAuthenticated, message.id, userInteracted]);
  
  // Verificar estado de like
  useEffect(() => {
    if (userInteracted || initialRender) return;
    
    const checkMessageLikeStatus = async () => {
      try {
        if (isAuthenticated) {
          let messageId = message.id;
          if (typeof messageId === 'object' && messageId._id) {
            messageId = messageId._id;
          }
          
          const result = await messagesService.checkLikeStatusBatch(messageId);
          if (result && !result.pending) {
            setLiked(result.liked);
            // Actualizar el contador solo si viene del servidor
            if (typeof result.likes_count === 'number') {
              setLikesCount(result.likes_count);
            }
            
            try {
              sessionStorage.setItem(`message_like_${messageId}`, result.liked ? 'true' : 'false');
            } catch (storageError) {
              console.warn('Could not store like status in session storage:', storageError);
            }
          }
        }
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };
    
    const timer = setTimeout(() => {
      if (isAuthenticated && message.id) {
        checkMessageLikeStatus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [message.id, isAuthenticated, userInteracted, initialRender]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      setLiked(false);
      setLikesCount(initialLikesCount); // Usar el valor inicial del servidor
      setShared(false);
    }
  }, [isAuthenticated, initialLikesCount]);

  // Add WebSocket listener for like updates
  useEffect(() => {
    const handleWebSocketMessage = (data) => {
      if (data.type === 'like_update' && data.message && data.message.id === message.id) {
        if (!data.is_current_user) {
          // Confiar en los valores del servidor
          setLikesCount(data.message.likes_count || data.message.likes || 0);
          if (data.message.liked !== undefined) {
            setLiked(data.message.liked);
          }
        }
      }
    };
    
    const unsubscribe = addMessageHandler(handleWebSocketMessage);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [message.id, addMessageHandler]);

  const handleLikeClick = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    
    if (likeButtonCooldown) {
      return;
    }
    
    setUserInteracted(true);
    setLikeButtonCooldown(true);
    
    try {
      let messageId = message.id;
      if (typeof messageId === 'object' && messageId._id) {
        messageId = messageId._id;
      }
      
      const action = liked ? 'unlike' : 'like';
      const newLikedState = !liked;
      
      // Optimistic update
      setLiked(newLikedState);
      setLikesCount(prev => newLikedState ? prev + 1 : Math.max(0, prev - 1));
      
      // Store in session storage
      try {
        const storageKey = `message_like_${messageId}`;
        sessionStorage.setItem(storageKey, newLikedState ? 'true' : 'false');
      } catch (storageError) {
        console.warn('Could not save like state to session storage:', storageError);
      }
      
      // Notify parent if needed
      if (onLikeUpdate) {
        onLikeUpdate(messageId, newLikedState, newLikedState ? likesCount + 1 : Math.max(0, likesCount - 1));
      }
      
      // Make API call
      const result = await messagesService.likeMessage(messageId, action);
      
      // Update with server response
      if (result) {
        if (result.liked !== undefined) {
          setLiked(result.liked);
        }
        if (typeof result.likes_count === 'number') {
          setLikesCount(result.likes_count);
        }
        
        // Update session storage with server state
        try {
          const storageKey = `message_like_${messageId}`;
          sessionStorage.setItem(storageKey, result.liked ? 'true' : 'false');
        } catch (storageError) {
          console.warn('Could not update like state in session storage:', storageError);
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update on error
      setLiked(liked);
      setLikesCount(initialLikesCount);
    } finally {
      setTimeout(() => {
        setLikeButtonCooldown(false);
      }, 400);
    }
  };
  
  const handleShareClick = async () => {
    try {
      if (isAuthenticated) {
        let messageId = message.id;
        if (typeof messageId === 'object' && messageId._id) {
          messageId = messageId._id;
        }
        
        // Only share if not already shared
        if (!shared) {
          // Don't update UI optimistically anymore - wait for server response
          const result = await messagesService.shareMessage(messageId);
          
          // Only update UI if server confirms the share was successful
          // The API should return information about whether the share was actually counted
          if (result && result.success) {
            setShared(true);
            setSharesCount(prev => prev + 1);
          }
        }
      }
      
      // Create a new container just for the content to be captured
      const contentContainer = document.createElement('div');
      const isDarkTheme = theme.body === '#18191a';
      
      // Style the container
      contentContainer.style.background = isDarkTheme ? '#242526' : '#ffffff';
      contentContainer.style.color = isDarkTheme ? '#e4e6eb' : '#1c1e21';
      contentContainer.style.padding = '20px';
      contentContainer.style.borderRadius = '12px';
      contentContainer.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
      contentContainer.style.position = 'fixed';
      contentContainer.style.top = '-9999px';
      contentContainer.style.left = '-9999px';
      contentContainer.style.width = '500px';
      contentContainer.style.maxWidth = '90%';
      
      // Add sapito header (avatar, name, time)
      const headerDiv = document.createElement('div');
      headerDiv.style.display = 'flex';
      headerDiv.style.marginBottom = '15px';
      headerDiv.style.alignItems = 'center';
      
      // Sapito avatar
      const avatarDiv = document.createElement('div');
      avatarDiv.style.width = '40px';
      avatarDiv.style.height = '40px';
      avatarDiv.style.borderRadius = '50%';
      avatarDiv.style.backgroundColor = theme.primary;
      avatarDiv.style.color = 'white';
      avatarDiv.style.display = 'flex';
      avatarDiv.style.alignItems = 'center';
      avatarDiv.style.justifyContent = 'center';
      avatarDiv.style.fontWeight = 'bold';
      avatarDiv.style.marginRight = '10px';
      avatarDiv.innerHTML = '🐸';
      headerDiv.appendChild(avatarDiv);
      
      // User info container
      const userInfoDiv = document.createElement('div');
      userInfoDiv.style.display = 'flex';
      userInfoDiv.style.flexDirection = 'column';
      userInfoDiv.style.justifyContent = 'center';
      
      // Username
      const usernameDiv = document.createElement('div');
      usernameDiv.style.fontWeight = 'bold';
      usernameDiv.style.marginBottom = '2px';
      usernameDiv.textContent = 'Universitario Anónimo';
      userInfoDiv.appendChild(usernameDiv);
      
      // Timestamp - Fix the invalid date issue by using created_at property
      const timeDiv = document.createElement('div');
      timeDiv.style.fontSize = '12px';
      timeDiv.style.color = isDarkTheme ? '#b0b3b8' : '#65676b';
      
      // Use created_at instead of timestamp, and handle potential date format issues
      try {
        if (message.created_at) {
          timeDiv.textContent = formatTimeAgo(new Date(message.created_at));
        } else {
          timeDiv.textContent = 'Hace un momento';
        }
      } catch (e) {
        console.error('Error formatting date:', e);
        timeDiv.textContent = 'Hace un momento';
      }
      
      userInfoDiv.appendChild(timeDiv);
      
      headerDiv.appendChild(userInfoDiv);
      contentContainer.appendChild(headerDiv);
      
      // Clone just the content
      if (message.text) {
        const textElement = document.createElement('div');
        textElement.style.fontSize = '16px';
        textElement.style.lineHeight = '1.5';
        textElement.style.marginBottom = message.image ? '15px' : '0';
        textElement.style.wordBreak = 'break-word';
        textElement.innerHTML = message.text.replace(/#(\w+)/g, '<span style="color:#1877f2;font-weight:bold;">#$1</span>');
        contentContainer.appendChild(textElement);
      }
      
      // Clone the image if present
      if (message.image) {
        const imageElement = document.createElement('img');
        imageElement.src = message.image;
        imageElement.crossOrigin = "anonymous";
        imageElement.style.width = '100%';
        imageElement.style.borderRadius = '8px';
        imageElement.style.marginTop = '10px';
        contentContainer.appendChild(imageElement);
      }
      
      // Add watermark
      const watermark = document.createElement('div');
      const universityText = currentUniversity ? ` | ${currentUniversity.shortName}` : '';
      watermark.innerHTML = `🐸 Sapotix.com${universityText}`;
      watermark.style.position = 'absolute';
      watermark.style.top = '10px';
      watermark.style.right = '10px';
      watermark.style.fontSize = '12px';
      watermark.style.color = isDarkTheme ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
      watermark.style.display = 'flex';
      watermark.style.alignItems = 'center';
      watermark.style.gap = '4px';
      watermark.style.zIndex = '5';
      contentContainer.appendChild(watermark);
      
      document.body.appendChild(contentContainer);
      
      // Wait for images to load completely
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(contentContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: isDarkTheme ? '#242526' : '#ffffff',
        logging: false,
        imageTimeout: 0, // No timeout for image loading
        onclone: (document) => {
          // Additional processing in the cloned document if needed
          const images = document.querySelectorAll('img');
          images.forEach(img => {
            img.crossOrigin = "anonymous";
          });
        }
      });
      
      document.body.removeChild(contentContainer);
      
      const imageUrl = canvas.toDataURL('image/png');
      setShareImageUrl(imageUrl);
      setShowShareModal(true);
    } catch (error) {
      console.error('Error al compartir mensaje:', error);
    }
  };
  
  const downloadImage = () => {
    if (shareImageUrl) {
      const downloadLink = document.createElement('a');
      const fileName = `sapotix-share-${new Date().getTime()}.png`;
      
      downloadLink.href = shareImageUrl;
      downloadLink.download = fileName;
      downloadLink.click();
      
      setShowShareModal(false);
      setShareImageUrl(null);
    }
  };
  
  const closeShareModal = () => {
    setShowShareModal(false);
    setShareImageUrl(null);
  };
  
  const toggleComments = () => {
    setShowComments(!showComments);
  };
  
  const processText = (text) => {
    if (!text) return '';
    
    // Check if message has hashtags array
    if (message.hashtags && message.hashtags.length > 0) {
      // Crear un regex que coincida específicamente con los hashtags conocidos
      // pero evitando coincidencias parciales (como #hashtag dentro de #hashtag123)
      const escapedHashtags = message.hashtags.map(tag => 
        tag.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&') // escapar caracteres especiales
      );
      const hashtagPattern = escapedHashtags.join('|');
      // Coincidir con el hashtag exacto
      const tempRegex = new RegExp(`#(${hashtagPattern})\\b`, 'g');
      
      // Dividir el texto por los hashtags
      const parts = [];
      let lastIndex = 0;
      let match;
      
      // Crear una copia del texto para manipularlo
      const processedText = text.replace(/#(\w+)#/g, '#$1'); // Convertir #texto# a #texto
      
      // Encontrar todas las coincidencias
      while ((match = tempRegex.exec(processedText)) !== null) {
        // Añadir el texto antes del hashtag
        if (match.index > lastIndex) {
          parts.push(processedText.substring(lastIndex, match.index));
        }
        
        // Añadir el hashtag como componente
        const hashtag = match[1];
        parts.push(
          <HashtagHighlight 
            key={`hashtag-${match.index}`}
            onClick={() => onHashtagClick(hashtag)}
          >
            #{hashtag}
          </HashtagHighlight>
        );
        
        lastIndex = match.index + match[0].length;
      }
      
      // Añadir el texto restante después del último hashtag
      if (lastIndex < processedText.length) {
        parts.push(processedText.substring(lastIndex));
      }
      
      return parts;
    }
    
    // Fallback a regex normal si no hay array de hashtags
    // Primero limpiamos formato incorrecto (#texto# -> #texto)
    const cleanedText = text.replace(/#([\wÁÉÍÓÚáéíóúÀÈÌÒÙàèìòùÄËÏÖÜäëïöüÑñ]+)#/g, '#$1');
    
    // Detectar hashtags con regex
    const hashtagRegex = /#([\wÁÉÍÓÚáéíóúÀÈÌÒÙàèìòùÄËÏÖÜäëïöüÑñ]+)\b/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Encontrar todas las coincidencias
    while ((match = hashtagRegex.exec(cleanedText)) !== null) {
      // Añadir el texto antes del hashtag
      if (match.index > lastIndex) {
        parts.push(cleanedText.substring(lastIndex, match.index));
      }
      
      // Añadir el hashtag como componente
      const hashtag = match[1];
      parts.push(
        <HashtagHighlight 
          key={`hashtag-${match.index}`}
          onClick={() => onHashtagClick(hashtag)}
        >
          #{hashtag}
        </HashtagHighlight>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Añadir el texto restante después del último hashtag
    if (lastIndex < cleanedText.length) {
      parts.push(cleanedText.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : cleanedText;
  };
  
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleImageClick = (e) => {
    if (!zoomed) {
      // Calcular el punto de origen del zoom basado en dónde se hizo clic
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setTransformOrigin(`${x}% ${y}%`);
    }
    setZoomed(!zoomed);
  };

  return (
    <>
      <GlobalStyle />
      <MessageContainer ref={messageRef} $initialRender={initialRender}>
        <UserAvatar>
          🐸
        </UserAvatar>
        <ContentWrapper>
          <MessageHeader>
            <Username>Universitario Anónimo</Username>
            <Timestamp>{formatTimeAgo(message.created_at)}</Timestamp>
          </MessageHeader>
          
          <MessageText $hasImage={message.image}>
            {processText(message.text)}
          </MessageText>
          
          {message.image && (
            <ImagePreviewContainer>
              <MessageImage 
                src={message.image} 
                alt="Contenido compartido"
                onClick={() => setShowFullImage(true)}
                crossOrigin="anonymous"
              />
            </ImagePreviewContainer>
          )}
          
          <ActionBar>
            <ActionButton 
              onClick={handleLikeClick}
              $active={liked}
              $activeColor="#e74c3c"
              $hoverAnimation="true"
              disabled={likeButtonCooldown}
            >
              {liked ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              )}
              <span className="action-text">Me gusta</span>
              {likesCount > 0 && <StatCount $active={liked} $activeColor="#e74c3c">({likesCount})</StatCount>}
            </ActionButton>
            
            <ActionButton 
              onClick={toggleComments}
              $active={showComments}
              $activeColor="#3498db"
              $hoverAnimation="true"
            >
              {showComments ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                </svg>
              )}
              <span className="action-text">Comentar</span>
              {message.comments_count > 0 && <StatCount $active={showComments}>({message.comments_count})</StatCount>}
            </ActionButton>
            
            <ActionButton 
              onClick={handleShareClick}
              $active={shared}
              $activeColor="#27ae60"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 9l1.41 1.41L11 5.83V22h2V5.83l4.59 4.59L19 9l-7-7-7 7z"/>
              </svg>
              <span className="action-text">Compartir</span>
              {sharesCount > 0 && <StatCount $active={shared}>({sharesCount})</StatCount>}
            </ActionButton>
          </ActionBar>
          
          {showComments && (
            <CommentList messageId={message.id} />
          )}
        </ContentWrapper>
      </MessageContainer>
      
      {showFullImage && 
        ReactDOM.createPortal(
          <FullImageOverlay onClick={() => setShowFullImage(false)}>
            <FullImageContainer $zoomed={zoomed}>
              <ImageWrapper 
                $zoomed={zoomed}
                ref={imageRef}
                onClick={(e) => e.stopPropagation()}
              >
                <FullImage 
                  src={message.image} 
                  alt="Contenido compartido a tamaño completo"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick(e);
                  }}
                  $zoomed={zoomed}
                  $transformOrigin={transformOrigin}
                  $isVector={message.image?.toLowerCase().endsWith('.svg')}
                  crossOrigin="anonymous"
                />
              </ImageWrapper>
              <ImageControls className="image-controls">
                <ControlButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullImage(false);
                  }}
                  title="Cerrar"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ControlButton>
                <ButtonGroup>
                  <ControlButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageClick(e);
                    }}
                    title={zoomed ? "Alejar" : "Acercar"}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {zoomed ? (
                        <path d="M21 21l-6-6m-2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10h-6" strokeLinecap="round" strokeLinejoin="round"/>
                      ) : (
                        <path d="M21 21l-6-6m-2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" strokeLinecap="round" strokeLinejoin="round"/>
                      )}
                    </svg>
                  </ControlButton>
                </ButtonGroup>
              </ImageControls>
            </FullImageContainer>
          </FullImageOverlay>,
          document.body
        )
      }
      
      {showAuthModal && 
        ReactDOM.createPortal(
          <AuthModal show={showAuthModal} onClose={closeAuthModal} initialTab="login" />,
          document.body
        )
      }
      
      {showShareModal && 
        ReactDOM.createPortal(
          <ShareModal>
            <ShareModalContent>
              <ShareModalHeader>
                <ShareModalTitle>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.15c-.05.21-.08.43-.08.66 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  ¡Comparte el chisme! 🔥
                </ShareModalTitle>
                <CloseButton onClick={closeShareModal}>×</CloseButton>
              </ShareModalHeader>
              <ShareModalBody>
                {shareImageUrl && (
                  <>
                    <ShareMessage>
                      ¡Captura lista para compartir! 📱
                    </ShareMessage>
                    <SharePreviewImage src={shareImageUrl} alt="Vista previa para compartir" crossOrigin="anonymous" />
                  </>
                )}
              </ShareModalBody>
              <ShareModalFooter>
                <CancelButton onClick={closeShareModal}>Cancelar</CancelButton>
                <ShareButton onClick={downloadImage}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/>
                  </svg>
                  Captura
                </ShareButton>
              </ShareModalFooter>
            </ShareModalContent>
          </ShareModal>,
          document.body
        )
      }
    </>
  );
};

const MessageItemWithStyles = ({ message, onHashtagClick, onLikeUpdate }) => (
  <>
    <GlobalStyle />
    <MessageItem message={message} onHashtagClick={onHashtagClick} onLikeUpdate={onLikeUpdate} />
  </>
);

export default MessageItemWithStyles;

